import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';

const PendingTourNotification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotification, setShowNotification] = useState(false);
  const [pendingTourId, setPendingTourId] = useState<string | null>(null);

  useEffect(() => {
    // Check if there's a pending tour
    const checkPendingTour = () => {
      const storedPendingTour = localStorage.getItem('pendingTourGeneration');
      if (storedPendingTour) {
        const pendingData = JSON.parse(storedPendingTour);
        setPendingTourId(pendingData.tourId);
        
        // Check if user has dismissed this specific tour notification
        const dismissedTourId = localStorage.getItem('dismissedPendingNotification');
        const isDismissed = dismissedTourId === pendingData.tourId;
        
        // Only show notification if NOT on collection page or generating page AND not dismissed
        const isOnCollectionPage = location.pathname === '/past-tours';
        const isOnGeneratingPage = location.pathname === '/generating';
        
        if (!isOnCollectionPage && !isOnGeneratingPage && !isDismissed) {
          setShowNotification(true);
        } else {
          setShowNotification(false);
        }
      } else {
        setShowNotification(false);
        setPendingTourId(null);
        // Clear dismissed notification when tour is complete
        localStorage.removeItem('dismissedPendingNotification');
      }
    };

    checkPendingTour();
    
    // Check every 5 seconds if tour is still pending
    const interval = setInterval(checkPendingTour, 5000);
    
    return () => clearInterval(interval);
  }, [location.pathname]);

  const handleNavigateToGeneration = () => {
    const storedPendingTour = localStorage.getItem('pendingTourGeneration');
    if (storedPendingTour) {
      const pendingData = JSON.parse(storedPendingTour);
      
      // Navigate back to generating screen with the stored state
      navigate('/generating', {
        state: {
          coordinates: pendingData.coordinates,
          interests: pendingData.interests,
          geocodeData: pendingData.geocodeData,
          selectedPlaces: pendingData.selectedPlaces,
          nearbyPlaces: pendingData.nearbyPlaces
        }
      });
    }
    setShowNotification(false);
  };

  const handleDismiss = () => {
    // Store the dismissed tour ID so it stays dismissed
    if (pendingTourId) {
      localStorage.setItem('dismissedPendingNotification', pendingTourId);
    }
    setShowNotification(false);
  };

  return (
    <AnimatePresence>
      {showNotification && pendingTourId && (
        <>
          {/* Backdrop to capture clicks */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleDismiss}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
            }}
          />
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          style={{
            position: 'fixed',
            top: 'max(56px, calc(env(safe-area-inset-top) + 12px))',
            left: '16px',
            right: '16px',
            marginLeft: 'auto',
            marginRight: 'auto',
            zIndex: 9999,
            maxWidth: '500px',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              padding: '16px 20px',
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer'
            }}
            onClick={handleNavigateToGeneration}
          >
            <AlertCircle size={20} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                Tour Research in Progress
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Tap to return to progress screen
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDismiss();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: '1',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <X size={20} />
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PendingTourNotification;

